import { useEffect, useState } from 'react';
import {
  fetchVendorActivity,
  EMPTY_VENDOR_ACTIVITY,
  type VendorActivity,
  type VendorNotification,
} from '@/lib/vendorNotifications';

// ─────────────────────────────────────────────────────────────
// Fil d'activité / notifications du vendeur.
// Affiche les commandes (dont celles via l'IA), les réservations et les
// consultations de ses contenus / de sa boutique par l'assistant IA.
// ─────────────────────────────────────────────────────────────

function timeAgo(dateStr: string): string {
  if (!dateStr) return '';
  const t = new Date(dateStr).getTime();
  if (Number.isNaN(t)) return '';
  const diff = Date.now() - t;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `il y a ${days} j`;
  return new Date(dateStr).toLocaleDateString('fr-FR');
}

function money(n: number | null | undefined, cur: string | null | undefined): string {
  if (n == null) return '';
  return `${Math.round(n).toLocaleString('fr-FR')} ${cur || 'MAD'}`;
}

interface Props {
  vendorId: number;
  /** Reçoit les compteurs agrégés (réservations, commandes IA, vues boutique). */
  onActivity?: (activity: VendorActivity) => void;
  limit?: number;
  title?: string;
}

export default function VendorNotificationsFeed({ vendorId, onActivity, limit = 14, title = 'Notifications & activité' }: Props) {
  const [activity, setActivity] = useState<VendorActivity>(EMPTY_VENDOR_ACTIVITY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    fetchVendorActivity(vendorId, limit)
      .then((a) => {
        if (!active) return;
        setActivity(a);
        onActivity?.(a);
      })
      .catch((e) => {
        if (active) setError(e instanceof Error ? e.message : 'Chargement impossible.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [vendorId, limit, onActivity]);

  const stats = [
    { icon: 'ri-shopping-bag-3-line', label: 'Commandes', value: activity.orderCount, tone: 'bg-secondary-100 text-secondary-900' },
    { icon: 'ri-robot-2-line', label: 'Via l\u2019IA', value: activity.aiOrderCount, tone: 'bg-accent-100 text-accent-700' },
    { icon: 'ri-calendar-check-line', label: 'Réservations', value: activity.reservationCount, tone: 'bg-accent-100 text-accent-700' },
    { icon: 'ri-store-2-line', label: 'Boutique vue (IA)', value: activity.boutiqueViews, tone: 'bg-primary-100 text-primary-700' },
  ];

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-4 md:px-5 py-4 border-b border-background-200/70">
        <span className="w-9 h-9 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
          <i className="ri-notification-3-line text-lg"></i>
        </span>
        <div>
          <h3 className="text-sm font-semibold text-foreground-900">{title}</h3>
          <p className="text-[11px] text-foreground-500">Commandes, réservations et consultations via l&apos;assistant IA</p>
        </div>
      </div>

      {/* Compteurs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-4 md:p-5 border-b border-background-200/70">
        {stats.map((s) => (
          <div key={s.label} className="rounded-lg bg-background-100/60 border border-background-200/70 p-3">
            <div className={`w-8 h-8 rounded-lg ${s.tone} flex items-center justify-center mb-2`}>
              <i className={`${s.icon} text-sm`}></i>
            </div>
            <div className="text-xl font-bold text-foreground-950 tabular-nums">{s.value.toLocaleString()}</div>
            <div className="text-[11px] text-foreground-500 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
        </div>
      ) : error ? (
        <div className="px-5 py-6 text-center text-sm text-red-600">{error}</div>
      ) : activity.notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
          <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mb-2">
            <i className="ri-notification-off-line text-xl text-foreground-400"></i>
          </div>
          <p className="text-sm text-foreground-500">Aucune activité pour le moment.</p>
          <p className="text-[11px] text-foreground-400 mt-1">Les commandes, réservations et consultations IA apparaîtront ici.</p>
        </div>
      ) : (
        <ul className="divide-y divide-background-200/70">
          {activity.notifications.map((n: VendorNotification) => (
            <li key={n.id} className="flex items-start gap-3 px-4 md:px-5 py-3">
              <span className={`w-9 h-9 rounded-full ${n.tone} flex items-center justify-center flex-shrink-0`}>
                <i className={`${n.icon}`}></i>
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-foreground-900">{n.title}</p>
                  {n.viaAi && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[10px] font-semibold">
                      <i className="ri-robot-2-line text-[10px]"></i>IA
                    </span>
                  )}
                </div>
                <p className="text-xs text-foreground-500 mt-0.5 line-clamp-2">{n.detail}</p>
              </div>
              <div className="text-right flex-shrink-0">
                {n.amount != null && (
                  <div className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{money(n.amount, n.currency)}</div>
                )}
                <div className="text-[11px] text-foreground-400 whitespace-nowrap">{timeAgo(n.date)}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}